import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Polygon, Marker, Popup, useMap, useMapEvents, LayersControl, LayerGroup, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useLocalization } from '../Context/MultiLanguageContext';
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import { getPamCodeSuggessionAsync } from '../../API/Address/GetPamCodeSuggessionAsync';
import OfficeIcon from '../../assets/icons/OfficeList.svg';
import OtherIcon from '../../assets/icons/OtherList.svg';
import HomeIcon from '../../assets/icons/HomeLIst.svg';
import ShareIcon from '../../assets/icons/share.svg';
import CopyIcon from '../../assets/icons/copy.svg';
import CheckIcon from '../../assets/icons/checked.svg';
import { EmailShareButton, WhatsappShareButton, FacebookShareButton, TwitterShareButton } from "react-share";
import { EmailIcon, WhatsappIcon, FacebookIcon, TwitterIcon } from "react-share";
import FavouriteTrue from '../../assets/icons/favouritetrue.svg';
import FavouriteFalse from '../../assets/icons/favouritefalse.svg';
import { AddtoFavouritesAsync } from "../../API/AddressFavourites/AddToFavouritesAsync";
import { RemoveFromFavouritesAsync } from "../../API/AddressFavourites/RemoveFromFavouritesAsync";
import MarkerTooltip from "./MarkerTooltip";
import ViewModal from './ViewModal';
import { Address as PamAddress } from '../../API/Address/GetPamCodeSuggessionAsync';

interface TokenPayload {
  RoleId: number;
}

interface PolygonMapComponentProps {
  addresses?: PamAddress[];
  boundaryData?: { latitude: number; longitude: number }[];
  markerLat?: number;
  markerLng?: number;
}

const MapDigipin: React.FC<PolygonMapComponentProps> = ({
  addresses = [],
  boundaryData = [],
  markerLat,
  markerLng
}) => {
  const [marker, setMarker] = useState<L.LatLng | null>(null);
  const [markerPosition, setMarkerPosition] = useState<[number, number] | null>(null);
  const [street, setStreet] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [pincode, setPincode] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useLocalization();
  const navigate = useNavigate();
  const [addressResponse, setAddressResponse] = useState<PamAddress[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeSharePamCode, setActiveSharePamCode] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<{ type: "pamCode" | "digiPin"; value: string } | null>(null);
  const [roleId, setRoleId] = useState<number | null>(null);

  const defaultCenter: [number, number] =
    addresses.length > 0
      ? [addresses[0].latitude, addresses[0].longitude]
      : [9.9921, 76.3019];

  const polygonPositions = addresses.map(address =>
    L.latLng(address.latitude, address.longitude)
  );
  const boundaryPositions = boundaryData.map(boundary =>
    L.latLng(boundary.latitude, boundary.longitude)
  );

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decodedToken = jwtDecode<TokenPayload>(token);
        setRoleId(Number(decodedToken.RoleId));
      } catch (error) {
        // console.error('Error decoding token:', error);
      }
    }
  }, []);

  useEffect(() => {
  }, [roleId]);

  useEffect(() => {
    if (!markerPosition) return;
    const [lat, lng] = markerPosition;
    fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`)
      .then(response => response.json())
      .then(data => {
        setPincode(data.address?.postcode || "N/A");
        setStreet(data.address?.road || "N/A");
        setCity(data.address?.city || data.address?.town || data.address?.village || "N/A");
      })
      .catch(() => {
        setPincode("");
        setStreet("");
        setCity("");
      });
  }, [markerPosition]);

  useEffect(() => {
    if (!isModalOpen) {
      setActiveSharePamCode(null);
    }
  }, [isModalOpen]);



  const handleCopy = async (value: string, type: "pamCode" | "digiPin") => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField({ type, value });
      setTimeout(() => setCopiedField(null), 2000);

    } catch (err) {
      // console.error('Failed to copy: ', err);

    }
  };


  const formatPamCode = (pamCode: string) => {
    if (!pamCode) return "N/A";
    const pincode = pamCode.slice(0, 6);
    const blockCode = pamCode.slice(6, 10);
    const addressCode = pamCode.slice(10);
    return `${pincode}-${blockCode}-${addressCode}`;
  };

  const shareMessage = (address: PamAddress) => {
    return `Check out this address:
    ${address.addressLine1}, ${address.addressLine2}, ${address.addressLine3}
    PAM Code: ${formatPamCode(address.pamCode || "")}
    DIGI PIN: ${address?.digiPin || "N/A"}
    
    More details: https://getmethere.in`;
  };

  const shareBlockCodeMessage = (pinCode: string, blockCode: string, digiPin?: string): string => {
    return `Block Code: ${pinCode}-${blockCode}\nDIGI PIN: ${digiPin || "N/A"}\n\nMore details: https://getmethere.in`;
  };

  const HeartClick = async (addressId: string, isFavourite: boolean) => {
    const newFavourite = !isFavourite;
    setAddressResponse((prevData) =>
      prevData.map((item) =>
        item.id === addressId ? { ...item, isFavourite: newFavourite } : item
      )
    );

    const token = localStorage.getItem('token');
    if (!token) {
      // console.error('Token not found. User might be logged out.');
      return;
    }

    try {
      const numericAddressId = Number(addressId);
      if (isNaN(numericAddressId)) {
        throw new Error("Invalid addressId: Cannot convert to number");
      }

      if (newFavourite) {
        await AddtoFavouritesAsync(numericAddressId, token);
      } else {
        await RemoveFromFavouritesAsync(numericAddressId, token);
      }
    } catch (error) {
      // console.error("An error occurred while updating favourite status:", error);
      revertFavouriteState(addressId, isFavourite);
    }
  };

  const revertFavouriteState = (addressId: string, originalState: boolean) => {
    setAddressResponse((prevData) =>
      prevData.map((item) =>
        item.id === addressId ? { ...item, isFavourite: originalState } : item
      )
    );
  };

  const toggleFavourite = (id?: string) => {
    if (!id) return;
    setAddressResponse((prev) =>
      prev.map((address) =>
        address.id === id ? { ...address, isFavourite: !address.isFavourite } : address
      )
    );
  };

  const goToMapAddAddress = (latitude: number, longitude: number) => {
    if (roleId === 3) {
      toast.info("Please Log In to Add Address", { toastId: "guest-login" });
      return;
    }
    const params = new URLSearchParams({
      latitude: latitude.toString(),
      longitude: longitude.toString(),
      addressLine1: street || '',
      addressLine2: city || '',
      addressLine3: `${street}, ${city}` || '',
      pinCode: pincode || '',
    });
    navigate(`/add-new-address?${params.toString()}`);
  };

  const fetchAddresses = async () => {
    setIsSubmitting(true);
    const token = localStorage.getItem("token");
    if (!token) {
      // console.error("Token not found");
      setIsSubmitting(false);
      return;
    }
    try {
      const data = await getPamCodeSuggessionAsync(
        markerPosition ? markerPosition[0] : 0,
        markerPosition ? markerPosition[1] : 0,
        token,
        t
      );
      if (data === null) {
        setIsSubmitting(false);
        return;
      }
      // console.log("Fetched addresses:", data);
      if (Array.isArray(data) && data.length > 0) {
        // Data is an array of addresses — set it directly.
        setAddressResponse(data as PamAddress[]);
      } else if (data && !Array.isArray(data)) {
        // If API returns a single address object, normalize it into an array.
        setAddressResponse([data as PamAddress]);
      } else {
        console.warn("No data returned from PAM Code suggestions API or data has unexpected shape:", data);
        setAddressResponse([]);
      }

      setIsSubmitting(false);
      setIsModalOpen(true);
    } catch (error) {
      // console.error("Error fetching addresses:", error);
      setAddressResponse([]);
      setIsSubmitting(false);
    }
  };

  const handleMarkerClick = (lat: number, lng: number) => {
    const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
    window.open(googleMapsUrl, '_blank');
  };

  const FitBoundsHandler = () => {
    const map = useMap();

    useEffect(() => {
      if (polygonPositions.length > 0) {
        const bounds = L.latLngBounds(polygonPositions);
        map.fitBounds(bounds);
      }
    }, [polygonPositions, map]);

    return null;
  };

  // Helper to move map to marker position
  const MapFocusMarker: React.FC<{ position: [number, number] }> = ({ position }) => {
    const map = useMap();

    useEffect(() => {
      map.setView(position, 19, { animate: true });
    }, [position, map]);

    return null;
  };

  useEffect(() => {
    if (markerLat && markerLng) {
      setMarker(L.latLng(markerLat, markerLng));
      setMarkerPosition([markerLat, markerLng]);
    }
  }, [markerLat, markerLng]);

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <MapContainer
        center={markerLat && markerLng ? [markerLat, markerLng] : defaultCenter}
        zoom={19}
        className="h-full w-full"
      >
        <LayersControl position="topright">
          <LayersControl.BaseLayer name="Default Map" checked>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer name="Satellite View">
            <LayerGroup>
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution="&copy; Esri"
              />
              \              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                attribution="&copy; Esri"
                opacity={0.8}
              />
            </LayerGroup>
          </LayersControl.BaseLayer>
        </LayersControl>

        {markerPosition && <MapFocusMarker position={markerPosition} />}

        {markerPosition && (
          <Marker position={markerPosition}>
            {/** Use MarkerTooltip for marker tooltip on click **/}
            {/** Show MarkerTooltip only when a state is true (e.g., markerTooltipOpen) **/}
            {isModalOpen === false && (
              <MarkerTooltip
                street={street}
                city={city}
                pincode={pincode}
                markerPosition={markerPosition}
                isSubmitting={isSubmitting}
                t={t}
                openGoogleMaps={(lat, lng) => {
                  const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
                  window.open(url, "_blank");
                }}
                goToMapAddAddress={goToMapAddAddress}
                fetchAddresses={fetchAddresses}
                onClose={() => setMarkerPosition(null)}
              />
            )}
            {/* Remove the old Popup tooltip UI here */}
          </Marker>
        )}



      </MapContainer>


      <ViewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        addressResponse={addressResponse}
        t={t}
        copiedField={copiedField}
        handleCopy={handleCopy}
        activeSharePamCode={activeSharePamCode}
        setActiveSharePamCode={setActiveSharePamCode}
        toggleFavourite={toggleFavourite}
        formatPamCode={formatPamCode}
        shareMessage={shareMessage}
        shareBlockCodeMessage={shareBlockCodeMessage}
      />

    </div>
  );
};

export default MapDigipin;
